import { I18n } from '../lang';
import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { Region } from '../../constants/lang';
import qs from 'qs';
import sha256 from 'crypto-js/sha256';
import { v4 as uuidv4 } from 'uuid';
import { ENV } from '../../constants/net';
import { isEmpty, memoizeFn } from './utils';
import { ApiError, HttpError } from './interface';
import { logger } from '../../core/metrics';
const LANG_STORAGE_KEY = 'lang';

/**
 * 用户类型枚举
 */
export enum EContactType {
  /** 访客 */
  VISITOR,
  /** 电话号码 */
  PHONENUMBER,
  /** 邮件 */
  EMAIL,
  /** Aid_Uid */
  AID_UID,
  /** 微信 */
  WECHAT,
}

/**
 * 响应code定义
 */
export enum EResultCode {
  /** Sign过期 */
  SIGN_EXPIRED = 5362,
  /** Token Error */
  TOKEN_ERROR = 100012,
  /** 调用下游失败 */
  REQUEST_ERROR = 10001,
  /** 重放攻击 */
  DUP_REQ_ERROR = 10098,
  /** 时间戳不符合预期 */
  TIME_STMP_ERROR = 10099,
}

/**
 * UserAuthAxios定义
 */
interface IUserAuthAxios extends AxiosRequestConfig {
  /** 用户 id */
  businessUserId: string;
  /** 环境 */
  curEnv: ENV;
  /** 区域 */
  region: Region;
  /** 业务线 id */
  businessLineId: number | string;
  /** 用户类型 */
  contactType: EContactType;
  /** 获取 sign 方法 */
  getSign: string | (() => Promise<string>);
  /** 是否是 Tob */
  isTob?: boolean;
  /** 是否本地 */
  isDev?: boolean;
  /** 多语言设置 */
  lang?: 'zh' | 'en' | 'pt' | 'es' | 'id' | string;
  /** error 错误信息提示处理 */
  onError?: (content: any) => void;

  apiOrigin?: string;
}

/**
 * 请求体config
 */
interface IUserAuthRequestConfig extends AxiosRequestConfig {
  /** 是否不处理返回体，直接返回response */
  pure?: boolean;
}

/**
 * 设置超时为10s
 */
const HTTP_TIMEOUT = 1000 * 10;

/**
 * 日志前缀
 */
const LOG_PREFIX = '[OCIC-User-Auth]';

const GATEWAY_ERROR_401_CODE = [
  EResultCode.TOKEN_ERROR,
  EResultCode.REQUEST_ERROR,
  EResultCode.DUP_REQ_ERROR,
  EResultCode.SIGN_EXPIRED,
  EResultCode.TIME_STMP_ERROR,
];

const GATEWAY_ERROR_DESC: {
  [key: string]: string;
} = {
  [EResultCode.TIME_STMP_ERROR]: I18n.t(
    'please_check_the_current_device_time__the_current_device_time_is_more_than_15_mi',
    {},
    '请检查当前设备时间，当前设备时间与本地时间相差超过15分钟。',
  ),
};

/**
 * 获取token算法
 * @param args
 */
const getToken = (args: Record<string, any>) => {
  const targetObj: Record<string, any> = {};
  Object.keys(args)
    .sort() //参数名ASCII码从小到大排序(字典序)
    .forEach((key) => {
      if (!isEmpty(args[key])) {
        targetObj[key] = args[key]; //参数名区分大小写；
      }
    });

  const str = qs.stringify(targetObj, { arrayFormat: 'comma', encode: false });
  return sha256(str).toString().toUpperCase();
};

/**
 * 获取缓存 Sign 方法
 * @param getSign
 * @param businessUserId
 * @param contactType
 */
const getMemoizeSignFn = ({
  getSign,
  businessUserId,
  contactType,
}: {
  businessUserId: string;
  contactType: EContactType;
  getSign: string | (() => Promise<string>);
}): (() => any) => {
  let signFn;
  if (typeof getSign === 'function') {
    signFn = getSign;
  } else {
    signFn = () => {
      return axios
        .get(getSign, {
          withCredentials: true,
          headers: {
            'x-customer-contact-type': contactType,
            'x-customer-business-user-id': businessUserId,
          },
        })
        .then((response) => {
          const { result, data, description } = response.data;
          if (result === 0 && data) {
            return data;
          }
          return Promise.reject({
            response,
            message: `getSign error,${description}`,
          });
        });
    };
  }

  return memoizeFn(signFn, 5000);
};

/**
 * 递归获取sign
 * @param memoizeGetSign
 * @param count
 */
const recursionGetSign = async (
  memoizeGetSign: () => Promise<string>,
  count: number = 1,
): Promise<string> => {
  try {
    return await memoizeGetSign();
  } catch (err: any) {
    logger.error(`${LOG_PREFIX}：recursionGetSign err`, err);
    if (count === 3) {
      throw err;
    }

    return recursionGetSign(memoizeGetSign, count + 1);
  }
};
const authRegx = /\/im-[a-z0-9]+\//;

/**
 * 用户侧Axios
 * @param options
 */
export const userAuthAxios = (options: IUserAuthAxios): AxiosInstance => {
  const {
    businessUserId,
    getSign,
    contactType,
    businessLineId,
    isTob = false,
    isDev = false,
    curEnv = ENV.PROD,
    region = Region.cn,
    apiOrigin = '',
    baseURL,
    lang,
    onError,
    ...rest
  } = options;

  let count = 1;
  if (
    isEmpty(businessUserId) ||
    isEmpty(businessLineId) ||
    isEmpty(contactType) ||
    isEmpty(getSign)
  ) {
    throw new Error(
      I18n.t(
        'businessuserid_businesslineid__contacttype__getsign_cannot_be_empty!',
        {},
        'businessUserId、businessLineId、contactType、getSign都不能为空！',
      ),
    );
  }

  // const targetEnv = curEnv === Env.BOE ? Env.BOE : Env.PROD;
  // const targetDomainMap = isTob ? DOMAIN_TOB_MAP[targetEnv] : DOMAIN_MAP[targetEnv];
  // const ocicDomain = isDev ? ['/'] : targetDomainMap[region];
  // if (isEmpty(ocicDomain)) {
  //   throw new Error(`${isTob ? '' : '非'}ToB场景，${region}区域${targetEnv}环境下不存在对应域名`);
  // }

  const memoizeGetSign = getMemoizeSignFn({
    getSign,
    businessUserId,
    contactType,
  });
  // const isDomain = baseURL && (baseURL.indexOf('http://') > -1 || baseURL.indexOf('https://') > -1);
  // const targetDomain = isDomain ? baseURL : resolve(ocicDomain.join('.'), baseURL || '');

  const signMap = new Map();
  const axiosClient = axios.create({
    timeout: HTTP_TIMEOUT,
    baseURL: baseURL,
    withCredentials: true,
    ...rest,
  });

  /**
   * 请求interceptors
   */
  axiosClient.interceptors.request.use(
    async (config: IUserAuthRequestConfig) => {
      if (config.url && config.url.indexOf('/im-c') === -1) {
        const depterPath = config.url?.match(authRegx);
        config.url = config.url?.replace(authRegx, depterPath?.[0] + 'im-c/');
      }
      if (!signMap.has('sign')) {
        const sign = await recursionGetSign(memoizeGetSign);
        signMap.set('sign', sign);
      }

      const timestamp = new Date().getTime();
      const uuid = uuidv4();
      const token = getToken({
        timestamp,
        contactType,
        businessLineId,
        businessUserId,
        nonce: uuid,
        sign: signMap.get('sign'),
      });

      const { headers = {} } = config;
      headers['x-customer-token'] = token;
      headers['x-customer-nonce'] = uuid;
      headers['x-customer-timestamp'] = timestamp;
      headers['x-customer-contact-type'] = contactType;
      headers['x-customer-business-user-id'] = businessUserId;
      headers['x-customer-business-line-id'] = businessLineId;
      headers['im-client-mark'] = 'im_client';

      const language =
        typeof window !== 'undefined' &&
        (lang || localStorage.getItem(LANG_STORAGE_KEY));

      if (language) {
        headers['lang'] = language;
      }

      logger.info('request_start', {
        path: config.url,
        sign: signMap.get('sign'),
      });
      return config;
    },
  );

  /**
   * 响应interceptors
   */
  axiosClient.interceptors.response.use(
    (response) => {
      const { result, description, code } = response.data;
      if (result === EResultCode.SIGN_EXPIRED && count < 4) {
        count++;
        signMap.delete('sign');
        return axiosClient(response.config);
      }

      count = 1;
      if ((response.config as IUserAuthRequestConfig).pure) {
        return response.data;
      }

      if (
        result === 0 ||
        code === 0 ||
        (response.config as any)?.notCheckService
      ) {
        return response.data;
      }

      if (GATEWAY_ERROR_401_CODE.includes(result)) {
        const httpError = new HttpError({
          description: GATEWAY_ERROR_DESC[result] || description,
          status: 401,
          path: response?.config?.url as string,
          logId: response?.headers?.['x-tt-logid'],
          response: response,
        });
        onError && onError(httpError);
        throw httpError;
      } else {
        const error = new ApiError({
          description,
          path: response.config.url!,
          logId: response.headers['x-tt-logid'],
          response: response.data || {},
        });

        throw error;
      }
    },
    (error) => {
      const description =
        error.response?.data?.description ||
        error.message ||
        error.response?.statusText;
      const httpError = new HttpError({
        description,
        status: error.response?.status,
        path: error.response?.config?.url,
        logId: error.response?.headers?.['x-tt-logid'],
        response: error.response,
      });
      onError && onError(httpError);

      throw error;
    },
  );

  return axiosClient;
};

export default userAuthAxios;
